function populateSuggestions(AutocompleteSuggestion, AutocompleteSessionToken, elementController) {
  const input = elementController.addressLookupTarget;
  const list = elementController.suggestionsListTarget;
  const itemTemplate = list.querySelector("li");
  const sessionToken = new AutocompleteSessionToken();

  let activeIndex = -1;
  let selectingWithKeyboard = false;

  input.addEventListener("input", async () => {
    const query = input.value;
    if (!query) {
      clearSuggestions(list);
      return;
    }

    try {
      const suggestions = await fetchSuggestions(AutocompleteSuggestion, query, sessionToken);
      renderSuggestions(suggestions, itemTemplate, list, elementController);
      clearAddressFields(elementController);
      activeIndex = -1; // reset when new list appears
    } catch (err) {
      // Handle error silently
    }
  });

  input.addEventListener("blur", async () => {
    if (selectingWithKeyboard) {
      selectingWithKeyboard = false;
      return;
    }
    const selectedValue = elementController.addressLookupTarget.value;
    const suggestions = Array.from(
      elementController.suggestionsListTarget.querySelectorAll("li span"),
    ).map((span) => span.textContent.trim());

    if (selectedValue && suggestions.length === 0 && !elementController.postcodeTarget.value) {
      input.value = "";
    }
  });

  input.addEventListener("keydown", (e) => {
    const items = Array.from(list.querySelectorAll("li"));
    if (!items.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      activeIndex = (activeIndex + 1) % items.length;
      updateActiveItem(items, activeIndex);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      activeIndex = (activeIndex - 1 + items.length) % items.length;
      updateActiveItem(items, activeIndex);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < items.length) {
        selectingWithKeyboard = true;
        items[activeIndex].click();
      }
    } else if (e.key === "Escape") {
      clearSuggestions(list);
    }
  });
}

function updateActiveItem(items, activeIndex) {
  items.forEach((item, i) => {
    const activeClasses = item.dataset.activeClasses?.split(" ") || [];
    if (i === activeIndex) {
      item.classList.add(...activeClasses);
      item.setAttribute("aria-selected", "true");
      item.scrollIntoView({ block: "nearest" });
    } else {
      item.classList.remove(...activeClasses);
      item.setAttribute("aria-selected", "false");
    }
  });
}

function clearSuggestions(list) {
  list.innerHTML = "";
  list.classList.add("hidden");
}

async function fetchSuggestions(AutocompleteSuggestion, query, sessionToken) {
  const request = {
    input: query,
    sessionToken: sessionToken,
    includedRegionCodes: ["AU"],
  };
  const result = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
  return result.suggestions || [];
}

function clearAddressFields(elementController) {
  elementController.latitudeTarget.value = "";
  elementController.longitudeTarget.value = "";
  elementController.stateTarget.value = "";
  elementController.cityTarget.value = "";
  elementController.firstlineTarget.value = "";
  elementController.postcodeTarget.value = "";
  elementController.addressLookupTarget.setCustomValidity("");
}

function renderSuggestions(suggestions, itemTemplate, list, elementController) {
  clearSuggestions(list);

  suggestions.forEach((suggestion) => {
    const clonedItem = createSuggestionItem(suggestion, itemTemplate);
    clonedItem.addEventListener("click", () =>
      handleSuggestionClick(suggestion, elementController, list),
    );
    list.appendChild(clonedItem);
  });

  if (suggestions.length > 0) {
    list.classList.remove("hidden");
  }
}

function createSuggestionItem(suggestion, itemTemplate) {
  const clonedItem = itemTemplate.cloneNode(true);
  const mainText = suggestion.placePrediction?.mainText?.toString() || "";
  const secondaryText = suggestion.placePrediction?.secondaryText?.toString() || "";
  clonedItem.querySelector("span").innerHTML = `${mainText}, ${secondaryText}`;
  return clonedItem;
}

async function handleSuggestionClick(suggestion, elementController, list) {
  const targetsToDisable = [
    elementController.latitudeTarget,
    elementController.longitudeTarget,
    elementController.stateTarget,
    elementController.cityTarget,
    elementController.firstlineTarget,
    elementController.postcodeTarget,
  ];

  setFieldsDisabled(targetsToDisable, true);

  const placePrediction = suggestion.placePrediction;
  if (placePrediction) {
    const place = placePrediction.toPlace();
    await place.fetchFields({
      fields: ["location", "addressComponents", "formattedAddress"],
    });

    fillAddressFields(place, elementController);

    clearSuggestions(list);
    elementController.postcodeTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  setFieldsDisabled(targetsToDisable, false);
}

function setFieldsDisabled(fields, disabled) {
  fields.forEach((field) => {
    field.disabled = disabled;
  });
}

function fillAddressFields(place, elementController) {
  const getComponent = (type) =>
    place.addressComponents?.find((c) => c.types.includes(type))?.longText || "";

  elementController.latitudeTarget.value = place.location?.lat() || "";
  elementController.longitudeTarget.value = place.location?.lng() || "";
  elementController.addressLookupTarget.value = place.formattedAddress || "";
  elementController.stateTarget.value =
    place.addressComponents?.find((c) => c.types.includes("administrative_area_level_1"))
      ?.shortText || "";
  elementController.cityTarget.value =
    getComponent("locality") ||
    getComponent("postal_town") ||
    getComponent("administrative_area_level_2");
  elementController.postcodeTarget.value = getComponent("postal_code");
  const streetNumber = getComponent("street_number");
  const route = getComponent("route");
  const subpremise = getComponent("subpremise"); // e.g. unit/apartment number

  let firstLine = "";
  if (subpremise && streetNumber && route) {
    firstLine = `${subpremise}/${streetNumber} ${route}`;
  } else if (streetNumber && route) {
    firstLine = `${streetNumber} ${route}`;
  } else if (route) {
    firstLine = route;
  }
  elementController.firstlineTarget.value = firstLine;

  if (
    elementController.element.dataset.validateCompleteAddress &&
    elementController.element.dataset.validateCompleteAddress === "true"
  ) {
    const form = elementController.addressLookupTarget.closest("form");
    // skip validation if cancel toggle is visible or no form found
    if (
      (elementController.toggleCancelTarget &&
        !elementController.toggleCancelTarget.classList.contains("hidden")) ||
      !form
    ) {
      return;
    }

    validateCompleteAddress(form, elementController);
  }
}

function validateCompleteAddress(form, ec) {
  const requiredFields = [
    ec.addressLookupTarget,
    ec.firstlineTarget,
    ec.cityTarget,
    ec.stateTarget,
    ec.postcodeTarget,
  ];

  // Allow optional unit/subpremise before a slash, followed by the street number (e.g., "Unit J/170 Russel Street" or "170 Russel Street")
  const hasStreetNumber = /^([\w\s#.-]+\/)?\s*\d+/.test(ec.firstlineTarget.value.trim());

  // Determine if any required field is empty
  const isAnyEmpty = requiredFields.some((f) => !f.value || f.value.trim() === "");

  // Always set validity on address lookup if any fields are empty; otherwise enforce street number rule
  if (!hasStreetNumber) {
    ec.addressLookupTarget.setCustomValidity(
      "Please enter a street number and name (e.g. 123 Smith St).",
    );
  } else if (isAnyEmpty) {
    ec.addressLookupTarget.setCustomValidity("This field is required.");
  }

  // report validity immediately
  if (ec.addressLookupTarget.validationMessage)
    ec.addressLookupTarget.reportValidity && ec.addressLookupTarget.reportValidity();
}

export { populateSuggestions };
